import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PageWrapper from "@/components/PageWrapper";
import Particles from "@/components/Particles";
import PortfolioCard from "@/components/PortfolioCard";
import ProjectModal from "@/components/ProjectModal";
import { PROJECTS } from "@/data/projects";
import { cn } from "@/lib/utils";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "shopify", label: "Shopify" },
  { id: "wordpress", label: "WordPress" },
  { id: "custom", label: "Custom" },
];

export default function Portfolio() {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const filtered = useMemo(
    () =>
      filter === "all"
        ? PROJECTS
        : PROJECTS.filter((p) => p.category === filter),
    [filter],
  );

  return (
    <PageWrapper>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh" aria-hidden />
        <Particles count={28} />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-20 md:pt-28 pb-12 md:pb-20">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-400">
            Portfolio
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-5 font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight text-white"
          >
            Our <span className="text-gradient-accent">Work.</span>
          </motion.h1>
          <p className="mt-7 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed">
            A collection of projects delivered for businesses around the world —
            from Shopify storefronts and WordPress platforms to fully custom web
            applications.
          </p>
        </div>
      </section>

      <section className="relative border-t border-white/10">
        <div className="sticky top-16 md:top-20 z-30 backdrop-blur-xl bg-[#050505]/70 border-b border-white/10">
          <div className="mx-auto max-w-7xl px-6 md:px-10 py-4 md:py-5 flex items-center gap-2 md:gap-3 overflow-x-auto">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`portfolio-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "rounded-full px-5 py-2 text-sm transition-colors shrink-0 border",
                  filter === f.id
                    ? "bg-blue-500 border-blue-500 text-white"
                    : "border-white/15 text-zinc-300 hover:text-white hover:border-white/40",
                )}
              >
                {f.label}
                <span className="ml-2 text-xs opacity-60">
                  {f.id === "all"
                    ? PROJECTS.length
                    : PROJECTS.filter((p) => p.category === f.id).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-24">
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
          >
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => (
                <PortfolioCard
                  key={p.id}
                  project={p}
                  index={i}
                  onOpen={(proj) => {
                    setActive(proj);
                    setOpen(true);
                  }}
                />
              ))}
            </AnimatePresence>
          </motion.div>

          {filtered.length === 0 && (
            <p className="text-center text-zinc-500 py-20">No projects yet.</p>
          )}
        </div>
      </section>

      <ProjectModal project={active} open={open} onOpenChange={setOpen} />
    </PageWrapper>
  );
}
